import moment from 'moment';
import { useCallback, useMemo } from 'react';
import type { JourneyBarLabels, JourneyBarSearchParams } from 'voyager';
import { JourneyBar } from 'voyager';
import { useTranslations } from '@lang';
import type { OnChangeParams } from '../JourneyPicker/JourneyPicker.types';
import type { JourneyPickerV2Props } from './JourneyPickerV2.types';
import { CenterWrapper } from './JourneyPickerV2.styles';

export type {
  JourneyBarSearchParams,
  JourneyPickerV2Props,
  DateContext,
} from './JourneyPickerV2.types';

function useJourneyBarLabels(): JourneyBarLabels {
  const { t } = useTranslations();

  return useMemo(() => {
    const localeData = moment.localeData();
    const weekdays = localeData.weekdaysMin();
    const mondayFirst = [...weekdays.slice(1), weekdays[0]];
    const months = localeData.months();

    return {
      selectDate: t('spa.selectDate'),
      selectDates: t('common.dates'),
      search: t('spaClasses.search'),
      browse: t('common.category'),
      guests: (total: number) => t('common.numberOfGuests', { guestsAmount: total }),
      datePicker: {
        weekdays: mondayFirst,
        months,
        date: t('common.date'),
        checkIn: t('guestsPicker.checkin'),
        checkOut: t('guestsPicker.checkout'),
        selectDate: t('spa.selectDate'),
        previousMonth: t('aria.previousMonth'),
        nextMonth: t('aria.nextMonth'),
      },
      guestPicker: {
        guests: t('guestsPicker.label'),
        adults: t('guestsPicker.adultsLabel'),
        children: t('guestsPicker.childrenLabel'),
        summary: (adults: number, children: number) =>
          t('guestsPicker.value', {
            numberOfAdults: adults,
            numberOfChildren: children,
          }),
      },
    };
  }, [t]);
}

export function JourneyPickerV2({
  dateMode = 'range',
  dateContext = 'stay',
  categories,
  category,
  onCategoryChange,
  disableCategorySelect,
  journeyParams,
  changeJourneyParam,
  onChange,
  onSearchComplete,
  columnView,
}: JourneyPickerV2Props) {
  const labels = useJourneyBarLabels();

  const defaultFromDate =
    dateContext === 'spa' || dateContext === 'dining'
      ? journeyParams.spaDate?.format('YYYY-MM-DD') ||
        journeyParams.fromDate?.format('YYYY-MM-DD') ||
        ''
      : journeyParams.fromDate?.format('YYYY-MM-DD') || '';

  const defaultToDate = journeyParams.toDate?.format('YYYY-MM-DD') || '';

  const defaultSpaDate = journeyParams.spaDate?.format('YYYY-MM-DD') || '';

  const handleSearch = useCallback(
    (params: JourneyBarSearchParams) => {
      if (dateContext === 'stay') {
        changeJourneyParam('fromDate', params.fromDate);
        if (params.toDate) changeJourneyParam('toDate', params.toDate);
        changeJourneyParam('adults', params.adults);
        changeJourneyParam('children', params.children);
      } else if (dateContext === 'spa') {
        changeJourneyParam('spaDate', params.fromDate);
      } else if (dateContext === 'dining') {
        changeJourneyParam('diningDate', params.fromDate);
      }

      if (onChange) {
        const changeParams: OnChangeParams = {
          adults: params.adults ?? journeyParams.adults,
          children: params.children ?? journeyParams.children,
          fromDate: dateContext === 'stay' ? params.fromDate : defaultFromDate,
          toDate: dateContext === 'stay' ? params.toDate : defaultToDate,
          spaDate: dateContext === 'spa' ? params.fromDate : defaultSpaDate,
        };
        onChange(changeParams);
      }

      onSearchComplete?.(params);
    },
    [
      dateContext,
      changeJourneyParam,
      onChange,
      onSearchComplete,
      journeyParams.adults,
      journeyParams.children,
      defaultFromDate,
      defaultToDate,
      defaultSpaDate,
    ]
  );

  return (
    <CenterWrapper>
      <JourneyBar
        dateMode={dateMode}
        categories={categories}
        category={category}
        onCategoryChange={onCategoryChange}
        disableCategorySelect={disableCategorySelect}
        defaultFromDate={defaultFromDate}
        defaultToDate={dateMode === 'range' ? defaultToDate : undefined}
        defaultAdults={journeyParams.adults}
        defaultChildren={journeyParams.children}
        columnView={columnView}
        onSearch={handleSearch}
        labels={labels}
      />
    </CenterWrapper>
  );
}
